/**
 * BayReliabilityDetail
 *
 * Details for the selected bay. The bay's own reasons are listed first; reasons it
 * inherits from the site are marked as site-wide. The last tracked shot is a fact,
 * not a verdict, but a bay reported offline that is tracking shots is called out.
 */
import type {
  BayActivity,
  BayReliability,
  SiteReliabilityHint,
} from '../../models/reliabilityModels';
import {
  bayOwnReasons,
  classifyBayConcern,
  concernTone,
  describeTrackingMismatch,
  formatLastShotTracked,
  levelLabel,
  reasonLabel,
} from '../../utils/reliabilityPresentation';
import { StatusPill } from './StatusPill';

interface BayReliabilityDetailProps {
  readonly id: string;
  readonly label: string;
  readonly bay: BayReliability;
  readonly site: SiteReliabilityHint;
  readonly activity: BayActivity;
}

function activityLabel(activity: BayActivity): string {
  switch (activity) {
    case 'go-in-use':
      return 'Go in use now';
    case 'active':
      return 'Tracking shots now, no Go session';
    case 'idle':
      return 'No shots in the last 3 minutes';
    default: {
      const unhandled: never = activity;
      return unhandled;
    }
  }
}

export function BayReliabilityDetail({
  id,
  label,
  bay,
  site,
  activity,
}: BayReliabilityDetailProps): JSX.Element {
  const ownReasons = bayOwnReasons(bay, site);
  const inheritedReasons = bay.reasons.filter((reason) => !ownReasons.includes(reason));
  const mismatch = describeTrackingMismatch(bay, activity);
  const lastShot = formatLastShotTracked(bay.lastActivityAt, new Date());

  return (
    <section id={id} className="reliability-bay-detail" aria-label={`${label} details`}>
      <div className="reliability-bay-detail__header">
        <h4 className="reliability-bay-detail__title">{label}</h4>
        <StatusPill
          tone={concernTone(classifyBayConcern(bay, site))}
          label={levelLabel(bay.level)}
        />
      </div>

      {mismatch && (
        <p className="reliability-bay-detail__mismatch" role="note">
          {mismatch}
        </p>
      )}

      <div className="reliability-bay-detail__body">
        <div>
          {bay.reasons.length > 0 ? (
            <ul className="reliability-bay-detail__reasons">
              {ownReasons.map((reason) => (
                <li key={reason}>{reasonLabel(reason)}</li>
              ))}
              {inheritedReasons.map((reason) => (
                <li key={reason} className="reliability-bay-detail__reason--inherited">
                  {reasonLabel(reason)} (site-wide)
                </li>
              ))}
            </ul>
          ) : (
            <p className="reliability-bay-detail__note">
              {bay.level === 'Unknown'
                ? 'No recent upstream status to confirm this bay is fine.'
                : 'No problems detected for this bay.'}
            </p>
          )}
        </div>

        <dl className="reliability-facts">
          <div className="reliability-facts__row">
            <dt>Activity</dt>
            <dd>{activityLabel(activity)}</dd>
          </div>
          <div className="reliability-facts__row">
            <dt>Last shot tracked</dt>
            <dd title={lastShot.title}>{lastShot.text}</dd>
          </div>
          <div className="reliability-facts__row">
            <dt>Active sessions</dt>
            <dd>{bay.activeSessions ?? 'Not available'}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
